import React, { useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Sparkle, Cube, ChatCircleDots, Receipt, Key, Images, RocketLaunch,
  GithubLogo, ShieldCheck, ArrowRight, Lightning, CheckCircle,
} from "@phosphor-icons/react";
import { useAuth } from "@/context/AuthContext";

const HERO_IMG = "https://images.pexels.com/photos/26559582/pexels-photo-26559582.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const BUILDER_IMG = "https://images.unsplash.com/photo-1760536928911-40831dacdbc3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxkZXZlbG9wZXIlMjBjb2RpbmclMjBtaW5pbWFsaXN0JTIwc2V0dXB8ZW58MHx8fHwxNzg2OTc0MzM2fDA&ixlib=rb-4.1.0&q=85";
const TAX_IMG = "https://images.pexels.com/photos/5833877/pexels-photo-5833877.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940";
const MEDIA_IMG = "https://images.unsplash.com/photo-1765372860684-69f0e2524694?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTN8MHwxfHNlYXJjaHwzfHxjcmVhdGl2ZSUyMG1lZGlhJTIwc3R1ZGlvJTIwbGlnaHRpbmd8ZW58MHx8fHwxNzg2OTc0MzM2fDA&ixlib=rb-4.1.0&q=85";

const features = [
  { icon: Cube, title: "App Builder", desc: "Describe an app in plain words. Multi-agent orchestration writes React + Tailwind frontend, FastAPI backend, and MongoDB schema — with live preview." },
  { icon: ChatCircleDots, title: "AI Workspace", desc: "10+ models — Claude, GPT, Gemini — with Instant→Ultra effort levels, projects, and agent mode in one clean thread." },
  { icon: Receipt, title: "TaxOS", desc: "Five tax engines: ITR, GST, TDS, e-Invoice, Notices — official-schema JSON, reconciliations, and human-approved filings." },
  { icon: Key, title: "Universal API Key", desc: "One key → 10+ models plus image, video, OCR and reasoning endpoints. OpenAI-compatible with metering & billing." },
  { icon: Images, title: "Media Studio", desc: "Generate & edit images, and direct long-form 1–5 min video in 16:9 or 9:16 with Scene Director." },
  { icon: RocketLaunch, title: "Instant Deploy", desc: "One-click ship with instant SSL, custom domains, per-deploy versioning, rollback and health monitoring." },
];

const stack = ["React + TypeScript", "Tailwind CSS", "FastAPI", "MongoDB", "React Native / Expo", "Atlas Vector Search"];

export default function Landing() {
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    document.title = "Zeyrodha.AI — Full-stack AI app generation";
  }, []);

  const go = () => navigate(user ? "/dashboard" : "/login");

  return (
    <div className="min-h-screen bg-[#FCFAF8] text-[#2A2625] overflow-x-hidden">
      {/* Nav */}
      <header className="sticky top-0 z-50 backdrop-blur-xl bg-white/70 border-b border-white/40">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <img src="/clover-logo.png" alt="Zeyrodha.AI" className="w-8 h-8 object-contain" />
            <span className="font-display text-lg font-semibold tracking-tight">Zeyrodha<span className="text-[#2F9E5A]">.AI</span></span>
          </div>
          <nav className="hidden md:flex items-center gap-8 text-sm text-[#594F4D]">
            <a href="#features" className="hover:text-[#2A2625] transition-colors">Product</a>
            <a href="#stack" className="hover:text-[#2A2625] transition-colors">Stack</a>
            <a href="#ownership" className="hover:text-[#2A2625] transition-colors">Ownership</a>
          </nav>
          <button
            data-testid="nav-launch-btn"
            onClick={go}
            className="bg-[#FFB8A5] text-[#2A2625] hover:bg-[#FFCBBF] font-medium px-5 py-2.5 rounded-full transition-colors text-sm"
          >
            {user ? "Open dashboard" : "Sign in"}
          </button>
        </div>
      </header>

      {/* Hero */}
      <section className="relative z-glow">
        <img src={HERO_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-30" />
        <div className="absolute inset-0 bg-gradient-to-b from-white/70 via-[#FCFAF8]/80 to-[#FCFAF8]" />
        <div className="relative max-w-7xl mx-auto px-6 pt-24 pb-28">
          <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }} className="max-w-3xl">
            <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/70 border border-[#2A2625]/10 text-xs font-medium text-[#594F4D] mb-6">
              <Lightning size={14} weight="fill" className="text-[#F29A86]" /> Multi-agent · Web + Mobile · Zero lock-in
            </div>
            <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight leading-[1.05]">
              Describe it once.<br />Ship a full-stack app <span className="text-[#F29A86]">everywhere.</span>
            </h1>
            <p className="mt-6 text-lg text-[#594F4D] leading-relaxed max-w-2xl">
              Zeyrodha.AI orchestrates Architect, Designer, Developer and QA agents to generate production-grade
              React + FastAPI + MongoDB apps — web and mobile — with one-click integrations, GitHub ownership, and instant deployment.
            </p>
            <div className="mt-9 flex flex-wrap gap-4">
              <button
                data-testid="hero-start-btn"
                onClick={go}
                className="group bg-[#FFB8A5] text-[#2A2625] hover:bg-[#FFCBBF] font-medium px-7 py-3.5 rounded-full transition-colors flex items-center gap-2"
              >
                Start building <ArrowRight size={18} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
              </button>
              <a href="#features" className="border border-[#2A2625]/20 hover:bg-[#2A2625]/5 px-7 py-3.5 rounded-full font-medium transition-colors">
                Explore the platform
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Features bento */}
      <section id="features" className="max-w-7xl mx-auto px-6 py-24">
        <div className="max-w-2xl mb-14">
          <h2 className="font-display text-3xl sm:text-4xl font-semibold tracking-tight">One platform. Every layer of the build.</h2>
          <p className="mt-4 text-[#594F4D] text-lg">From idea to deployed product — orchestration, code, tax, media and APIs in a single peach-soft workspace.</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          {features.map((f, i) => (
            <motion.div
              key={f.title}
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              data-testid={`feature-card-${i}`}
              className="group bg-white/80 backdrop-blur-md border border-[#2A2625]/10 rounded-2xl p-8 hover:-translate-y-1 hover:shadow-[0_12px_48px_rgba(42,38,37,0.08)] transition-[transform,box-shadow] duration-300"
            >
              <div className="w-12 h-12 rounded-2xl bg-[#FFF4F0] flex items-center justify-center mb-5">
                <f.icon size={26} weight="duotone" className="text-[#F29A86]" />
              </div>
              <h3 className="font-display text-xl font-semibold mb-2">{f.title}</h3>
              <p className="text-[#594F4D] text-sm leading-relaxed">{f.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Showcase */}
      <section className="max-w-7xl mx-auto px-6 pb-8 grid md:grid-cols-2 gap-5">
        {[{ img: BUILDER_IMG, tag: "App Builder", t: "Live preview, iterate by chat", d: "Watch the file tree and preview update as agents write and QA your app in parallel." },
          { img: TAX_IMG, tag: "TaxOS", t: "Filing-grade, human-approved", d: "ITR/GST/TDS engines produce official-schema JSON with a 7-agent reviewer and mandatory approval." },
          { img: MEDIA_IMG, tag: "Media Studio", t: "Image & long-form video", d: "Generate assets and direct 1–5 minute cinematic scenes for web and mobile stores." }].map((c, i) => (
          <motion.div
            key={c.tag}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: i * 0.05 }}
            className={`relative rounded-3xl overflow-hidden border border-[#2A2625]/10 min-h-[280px] ${i === 0 ? "md:col-span-2" : ""}`}
          >
            <img src={c.img} alt={c.tag} className="absolute inset-0 w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-t from-[#2A2625]/85 via-[#2A2625]/25 to-transparent" />
            <div className="relative h-full flex flex-col justify-end p-8 text-white">
              <span className="text-xs font-medium tracking-wide uppercase text-[#FFCBBF]">{c.tag}</span>
              <h3 className="font-display text-2xl font-semibold mt-1">{c.t}</h3>
              <p className="text-white/80 text-sm mt-2 max-w-md">{c.d}</p>
            </div>
          </motion.div>
        ))}
      </section>

      {/* Stack */}
      <section id="stack" className="max-w-7xl mx-auto px-6 py-24">
        <div className="bg-white border border-[#2A2625]/10 rounded-3xl p-10 md:p-14">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div>
              <h2 className="font-display text-3xl font-semibold tracking-tight">One battle-tested stack. Fewer failures.</h2>
              <p className="mt-4 text-[#594F4D] leading-relaxed">
                Every generated app standardizes on React + Tailwind + FastAPI + MongoDB — cleaner AI code, reliable builds,
                and mobile output via React Native / Expo, ready for the Play Store and App Store.
              </p>
            </div>
            <div className="flex flex-wrap gap-3">
              {stack.map((s) => (
                <span key={s} className="px-4 py-2 rounded-full bg-[#FFF4F0] border border-[#2A2625]/10 text-sm font-medium font-mono">{s}</span>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Ownership */}
      <section id="ownership" className="max-w-7xl mx-auto px-6 pb-24">
        <div className="grid md:grid-cols-3 gap-5">
          {[{ icon: GithubLogo, t: "GitHub push", d: "Ship code to your own repo with commit history per iteration." },
            { icon: ShieldCheck, t: "Instant SSL + domains", d: "Deploy with HTTPS and connect a custom domain with guided DNS." },
            { icon: CheckCircle, t: "100% ownership", d: "Full zip export anytime. Real, readable, standard code — no lock-in." }].map((c) => (
            <div key={c.t} className="bg-white border border-[#2A2625]/10 rounded-2xl p-8">
              <c.icon size={28} weight="duotone" className="text-[#F29A86] mb-4" />
              <h3 className="font-display text-lg font-semibold">{c.t}</h3>
              <p className="text-[#594F4D] text-sm mt-2">{c.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 pb-28">
        <div className="relative overflow-hidden rounded-3xl bg-[#2A2625] text-white p-12 md:p-16 text-center">
          <div className="absolute -top-24 -right-16 w-80 h-80 rounded-full bg-[#FFB8A5]/30 blur-3xl" />
          <h2 className="relative font-display text-3xl sm:text-4xl font-semibold tracking-tight">Build your first app in minutes.</h2>
          <p className="relative mt-4 text-white/70 max-w-xl mx-auto">Sign in with Google and put the multi-agent builder, AI workspace and TaxOS to work.</p>
          <button
            data-testid="cta-start-btn"
            onClick={go}
            className="relative mt-8 bg-[#FFB8A5] text-[#2A2625] hover:bg-[#FFCBBF] font-medium px-8 py-3.5 rounded-full transition-colors inline-flex items-center gap-2"
          >
            Get started free <ArrowRight size={18} weight="bold" />
          </button>
        </div>
      </section>

      <footer className="border-t border-[#2A2625]/10">
        <div className="max-w-7xl mx-auto px-6 py-10 flex flex-col md:flex-row items-center justify-between gap-4 text-sm text-[#8C8381]">
          <div className="flex items-center gap-2">
            <div className="w-6 h-6 rounded-md bg-[#FFB8A5] flex items-center justify-center"><Sparkle size={13} weight="fill" className="text-[#2A2625]" /></div>
            <span className="font-display font-semibold text-[#2A2625]">Zeyrodha.AI</span>
          </div>
          <p>© 2026 Zeyrodha.AI — full-stack generation with total code ownership.</p>
        </div>
      </footer>
    </div>
  );
}
